import { ArrowRight, LayoutDashboard } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import { updateGoalProgress } from "wasp/client/operations";
import useTranslation from "../../client/hooks/useTranslation";
import { useLiteData } from "../data/LiteDataProvider";
import { previousWindow } from "../lib/filters";
import { byDay, closedOnly, equityCurve, groupBy, maxDrawdown, net, summarize } from "../lib/stats";
import type { Period } from "../lib/types";
import { EmptyStart } from "../onboarding/EmptyStart";
import { GettingStarted } from "../onboarding/GettingStarted";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { CountBars, EquityChart, PnlBars, UnderwaterChart } from "../ui/Charts";
import { HourHeatmap } from "../ui/Heatmap";
import { LiteCalendar } from "./dashboard/LiteCalendar";
import { TradeDrawer } from "../ui/TradeDrawer";
import { SideTag } from "../ui/TradeList";
import { Bar, Card, Empty, Pnl, Segmented, cardCls } from "../ui/ui";
import { KpiTiles } from "./dashboard/KpiTiles";
import { BehaviorCard, PropAccountsCard, TodayCard } from "./dashboard/SideCards";
import { MiniTable, RecentTable } from "./dashboard/Tables";

const PERIOD_LABEL: Record<Period, "today" | "week" | "month" | "threeMonths" | "sixMonths" | "all"> = {
  today: "today", week: "week", month: "month", "3m": "threeMonths", "6m": "sixMonths", all: "all",
};
const LOW_DATA = 20;

export default function LiteDashboardPage() {
  const t = useTranslation().lite;
  const d = t.dashboard;
  const { data: user } = useAuth();
  const { loading, fmt, trades, scopedTrades, filteredTrades, filters, strategies } = useLiteData();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const [curveMode, setCurveMode] = useState<"equity" | "daily">("equity");

  // Progression des objectifs recalculée à l'ouverture (la TodayCard l'affiche), comme en Advanced.
  const progressRefreshed = useRef(false);
  useEffect(() => {
    if (progressRefreshed.current) return;
    progressRefreshed.current = true;
    updateGoalProgress().catch(() => { /* valeurs en base conservées */ });
  }, []);

  const s = useMemo(() => summarize(filteredTrades), [filteredTrades]);
  const curve = useMemo(() => equityCurve(filteredTrades, fmt.locale), [filteredTrades, fmt.locale]);
  const days = useMemo(() => byDay(closedOnly(filteredTrades)), [filteredTrades]);
  const winDays = days.filter(x => x.pnl > 0).length;
  const maxDd = useMemo(() => maxDrawdown(filteredTrades), [filteredTrades]);
  const currentDd = curve.at(-1)?.drawdown ?? 0;
  const closedCount = useMemo(() => closedOnly(trades).length, [trades]);

  // Même indicateur sur la fenêtre précédente de même durée.
  const prev = useMemo(() => {
    const w = previousWindow(filters.period);
    if (!w) return null;
    return summarize(scopedTrades.filter(tr => { const at = new Date(tr.openedAt); return at >= w.from && at < w.to; }));
  }, [scopedTrades, filters.period]);

  const closed = useMemo(() => closedOnly(filteredTrades), [filteredTrades]);

  const distribution = useMemo(() => {
    const step = 100;
    const g = groupBy(closed, tr => Math.max(-6, Math.min(5, Math.floor(net(tr) / step))));
    return Array.from({ length: 12 }, (_, i) => i - 6).map(b => ({
      label: b === -6 ? d.distribution.below(fmt.num(-500, 0)) : b === 5 ? d.distribution.above(fmt.num(500, 0)) : fmt.num(b * step, 0),
      value: (g.get(b) ?? []).length * (b < 0 ? -1 : 1),
    }));
  }, [closed, d, fmt]);

  const weekday = useMemo(() => {
    const g = groupBy(closed, tr => (new Date(tr.openedAt).getDay() + 6) % 7);
    return t.common.weekdaysShort.slice(0, 5).map((label, i) => ({ label, value: Math.round((g.get(i) ?? []).reduce((a, tr) => a + net(tr), 0)) }));
  }, [closed, t]);

  const sides = useMemo(() => (["long", "short"] as const).map(side => ({ side, s: summarize(filteredTrades.filter(tr => tr.side === side)) })), [filteredTrades]);

  const bySymbol = useMemo(
    () => [...groupBy(closed, tr => tr.symbol).entries()].map(([k, ts]) => ({ key: k, label: <span className="font-semibold">{k}</span>, s: summarize(ts) })).sort((a, b) => b.s.netPnl - a.s.netPnl),
    [closed],
  );
  const byStrategy = useMemo(() => {
    // Un trade lié à plusieurs stratégies compte dans chacune.
    const m = new Map<string, typeof closed>();
    for (const tr of closed) for (const id of tr.strategyIds) m.set(id, [...(m.get(id) ?? []), tr]);
    return strategies.filter(st => m.has(st.id)).map(st => ({
      key: st.id,
      label: (
        <span className="inline-flex items-center gap-2 min-w-0 max-w-full">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.color }} />
          <span className="truncate">{st.name}</span>
        </span>
      ),
      s: summarize(m.get(st.id)!),
    })).sort((a, b) => b.s.netPnl - a.s.netPnl);
  }, [closed, strategies]);

  const h = new Date().getHours();
  const greeting = h < 6 ? d.greeting.night : h < 18 ? d.greeting.morning : d.greeting.evening;
  const name = user?.username || user?.email || "";
  const periodLabel = t.periods[PERIOD_LABEL[filters.period]];

  return (
    <>
      <PageHeader title={d.title} subtitle={d.subtitle(greeting, name, fmt.dateLong(new Date()))} />
      <PageBody>
        <GettingStarted />
        {closedCount > 0 && closedCount < LOW_DATA && (
          <div className="rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>{t.common.lowData}</span>
            <span className="flex items-center gap-2 flex-1 min-w-[160px] max-w-xs">
              <Bar value={closedCount / LOW_DATA} className="flex-1" />
              <span className="font-mono tabular-nums text-xs">{closedCount} / {LOW_DATA}</span>
            </span>
          </div>
        )}

        {loading ? (
          <div className={cardCls}><Empty title={t.common.loading} /></div>
        ) : !trades.length ? (
          <EmptyStart icon={LayoutDashboard} title={d.emptyTitle} text={d.emptyText} />
        ) : !filteredTrades.length ? (
          <div className={cardCls}><Empty title={t.common.noTradeInPeriod}>{t.common.noTradeInPeriodHint}</Empty></div>
        ) : (
          <>
            <KpiTiles s={s} prev={prev} days={days.length} winDays={winDays} sparkline={curve.map(c => c.equity)} maxDd={maxDd} currentDd={currentDd} />

            {/* Courbe + calendrier, à parts égales : le calendrier est l'outil de revue quotidien. */}
            <div className="grid xl:grid-cols-2 gap-5">
              <Card
                className="flex flex-col"
                bodyClassName="flex-1 flex flex-col justify-center"
                title={curveMode === "equity" ? d.curve.equity : d.curve.daily}
                subtitle={periodLabel}
                action={<Segmented size="sm" value={curveMode} onChange={setCurveMode} options={[{ id: "equity", label: d.curve.cumul }, { id: "daily", label: d.curve.perDay }]} />}
              >
                {curveMode === "equity" ? (
                  <>
                    <EquityChart data={curve} height={380} />
                    <div className="text-[11px] text-muted-foreground/70 mt-3 mb-1 pl-12">{d.curve.drawdown}</div>
                    <UnderwaterChart data={curve} height={90} />
                  </>
                ) : <PnlBars data={curve.map(c => ({ label: c.label, value: Math.round(c.pnl) }))} height={430} />}
              </Card>
              {/* Le calendrier de l'Advanced, à l'identique (charte et informations). */}
              <div className="min-w-0 [&>*]:rounded-2xl"><LiteCalendar /></div>
            </div>

            {/* Répartition */}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              <Card title={d.distribution.title} subtitle={d.distribution.subtitle}>
                <CountBars data={distribution} height={200} />
              </Card>
              <Card title={d.sides.title}>
                <div className="space-y-5">
                  {sides.map(({ side, s: ss }) => (
                    <div key={side}>
                      <div className="flex items-center justify-between">
                        <SideTag side={side} />
                        <Pnl value={ss.netPnl} className="font-semibold" />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-2 mb-1.5">
                        <span>{t.common.trades(ss.count)}</span>
                        <span className="font-mono tabular-nums">{d.sides.winners(fmt.pct(ss.winRate))}</span>
                      </div>
                      <Bar value={ss.winRate} tone={ss.winRate >= 0.5 ? "gain" : "warn"} />
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground pt-1">
                    {sides[0].s.netPnl >= sides[1].s.netPnl ? d.sides.longsBetter : d.sides.shortsBetter}
                  </p>
                </div>
              </Card>
              <Card title={d.weekday.title} className="md:col-span-2 xl:col-span-1">
                <PnlBars data={weekday} height={200} />
              </Card>
            </div>

            {/* Horaires + symboles + stratégies */}
            <div className="grid xl:grid-cols-12 gap-5">
              <Card title={d.hours.title} subtitle={d.hours.subtitle} className="xl:col-span-6">
                <HourHeatmap trades={filteredTrades} />
              </Card>
              <MiniTable className="xl:col-span-3" title={d.bySymbol} rows={bySymbol} empty={t.common.noTrade} />
              <MiniTable className="xl:col-span-3" title={d.byStrategy} rows={byStrategy} empty={d.noStrategyData} />
            </div>

            {/* Journée, comptes, comportement */}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              <TodayCard />
              <PropAccountsCard />
              <BehaviorCard trades={filteredTrades} className="md:col-span-2 xl:col-span-1" />
            </div>

            {/* Derniers trades */}
            <Card
              title={d.recent.title}
              pad={false}
              action={
                <Link to="/trading-journal" className="inline-flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap">
                  {d.recent.openJournal} <ArrowRight size={12} />
                </Link>
              }
            >
              <RecentTable trades={filteredTrades.slice(0, 10)} onSelect={setSelected} />
            </Card>
          </>
        )}
      </PageBody>
      <TradeDrawer tradeId={selected} onClose={() => setSelected(null)} />
    </>
  );
}
